// CP862 (IBM Hebrew DOS) byte → Unicode character mapping for the Hebrew alphabet range
const CP862 = {};
const HEB_LETTERS = "אבגדהוזחטיךכלםמןנסעףפץצקרשת";
for (let i = 0; i < HEB_LETTERS.length; i++) {
  CP862[0x80 + i] = HEB_LETTERS[i];
}

function decodeBytes(bytes) {
  return Array.from(bytes)
    .map(b => (b >= 0x80 && CP862[b]) ? CP862[b] : (b === 0x20 ? ' ' : (b >= 0x20 && b < 0x80 ? String.fromCharCode(b) : '')))
    .join('');
}

// Hebrew in these files is stored in visual order (right-to-left bytes).
// Reversing the trimmed string converts to logical Unicode order for RTL rendering.
function decodeHebField(bytes) {
  const raw = decodeBytes(bytes).trimStart();
  return [...raw].reverse().join('').trim();
}

function readBalance(bytes) {
  const s = String.fromCharCode(...bytes).trim();
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n / 100; // ILD stores amounts in agorot
}

// Decode a byte range to a "visual" string: digits stay as digits,
// space stays as space, CP862 Hebrew bytes (0x80-0x9A) become Hebrew
// letters in VISUAL order (not yet reversed). Other bytes → space.
function decodeVisual(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b >= 0x30 && b <= 0x39) out += String.fromCharCode(b);
    else if (b === 0x20) out += " ";
    else if (b >= 0x80 && CP862[b]) out += CP862[b];
    else out += " ";
  }
  return out;
}

// Reverse a visual-order Hebrew string into logical (readable) order.
function reverseHeb(s) {
  return [...s.trim()].reverse().join("").trim();
}

// Format an 8-digit DDMMYYYY string to DD/MM/YYYY (or null if invalid).
function formatDOB(d8) {
  if (!/^\d{8}$/.test(d8)) return null;
  const dd = d8.slice(0, 2), mm = d8.slice(2, 4), yyyy = d8.slice(4, 8);
  const di = +dd, mi = +mm, yi = +yyyy;
  if (di < 1 || di > 31 || mi < 1 || mi > 12 || yi < 1900 || yi > 2100) return null;
  return `${dd}/${mm}/${yyyy}`;
}

// Parse the SP (Settlement Plans) file → Map memberId → personal info.
// SP record layout (2000 bytes, verified across rows):
//   pos 6-15 : member ID (10 digits) — links to RM[0-9]
//   pos 16-24: employer code
//   pos 25-33: ת"ז (9 digits)
//   pos 43-57: last name  (visual Hebrew, right-aligned)
//   pos 58-72: first name (visual Hebrew, right-aligned)
//   city / zip(5) / gender(ז|נ) / DOB(DDMMYYYY) found via regex on the line
function parseSPFile(content) {
  const bytes = new Uint8Array(content);
  const map = new Map();
  const LINE_LEN = 2000;
  const stride = (bytes[2000] === 0x0D) ? 2002 : 2001;

  for (let offset = 0; offset + LINE_LEN <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + LINE_LEN);
    const memberId = String.fromCharCode(...line.subarray(6, 16));
    if (!/^\d{10}$/.test(memberId)) continue;

    const tazRaw = String.fromCharCode(...line.subarray(25, 34));
    const taz = /^\d{9}$/.test(tazRaw) && tazRaw !== "000000000" ? tazRaw : null;

    const lastName = reverseHeb(decodeVisual(line.subarray(43, 58)));
    const firstName = reverseHeb(decodeVisual(line.subarray(58, 73)));

    // City is a fixed field at cols 113-127 (right before the 5-digit zip at col 128).
    const city = reverseHeb(decodeVisual(line.subarray(113, 128))) || null;

    // gender (ז|נ) + DOB (DDMMYYYY) sit right after the zip; locate via regex.
    const region = decodeVisual(line.subarray(128, 160));
    let gender = null, birthDate = null;
    const m = region.match(/\d{5}([זנ])\d{2}(\d{8})/);
    if (m) {
      gender = m[1] === "ז" ? "זכר" : "נקבה";
      birthDate = formatDOB(m[2]);
    }

    map.set(memberId, {
      taz,
      firstName: firstName || null,
      lastName: lastName || null,
      city: city || null,
      gender,
      birthDate,
    });
  }
  return map;
}

// Each record in the RM (master members) file is 2000 bytes wide.
// Field positions (0-indexed):
//   0–9   : member ID (10 ASCII digits) — NOT the ת"ז; ת"ז comes from SP file
//  85–99  : fund 1 name (15 bytes, right-aligned in visual Hebrew)
// 100–114 : fund 1 balance (15 ASCII digits)
// 115–129 : fund 2 name (15 bytes, right-aligned in visual Hebrew)
// 130–144 : fund 2 balance (15 ASCII digits)
// Pattern repeats every 30 bytes: name(15) + balance(15) starting at 85
const FUND_FIELD_START = 85;
const FUND_FIELD_STRIDE = 30; // name(15) + balance(15)
const MAX_FUNDS = 10;

function parseRMRecord(line, spMap) {
  const rawId = String.fromCharCode(...line.subarray(0, 10));
  if (!/^\d{10}$/.test(rawId)) return null;
  if (rawId === '0000000000') return null;

  const personal = spMap.get(rawId) || {};

  // ת"ז from SP; fallback to 9 digits (strip leading 0 if present, else strip trailing).
  const taz = personal.taz || (rawId.startsWith('0') ? rawId.slice(1) : rawId.slice(0, 9));

  // Sub-product code at cols 40-44, e.g. "בסט" (the "period" field 27-35 is a coverage code, not a date)
  const subProduct = reverseHeb(decodeVisual(line.subarray(40, 44))) || null;
  // startDate comes from SB file (injected via sbMap); not available in RM itself
  const startDate = null; // will be merged below if sbMap has data

  const funds = [];
  for (let i = 0; i < MAX_FUNDS; i++) {
    const nameStart = FUND_FIELD_START + i * FUND_FIELD_STRIDE;
    const balStart = nameStart + 15;
    if (balStart + 15 > 2000) break;

    const balance = readBalance(line.subarray(balStart, balStart + 15));
    if (balance === 0) continue;

    const name = decodeHebField(line.subarray(nameStart, nameStart + 15));
    
    // Attempt to map to a track ID based on the decoded name
    const n = (name || "").trim();
    let trackId = "";
    
    // Check against exact matches or partial matches in the map
    for (const [key, value] of Object.entries(hachsharaTrackMap)) {
      if (n.includes(key) || (key.includes("S&P") && n.toLowerCase().includes("s&p"))) {
        trackId = value;
        break;
      }
    }
    
    funds.push({ 
      name: name || `קרן ${i + 1}`, 
      balance,
      trackId,
      sugMutzar: 5,
      companyCode: "520023649" // Hachshara legal ID
    });
  }

  return {
    rawId,
    taz,
    firstName: personal.firstName || null,
    lastName: personal.lastName || null,
    city: personal.city || null,
    gender: personal.gender || null,
    birthDate: personal.birthDate || null,
    startDate,
    subProduct,
    funds,
  };
}

// Parse the SB (Summary Balances) file → Map memberId → { startDate }
// SB record layout (2000 bytes):
//   pos 6-15 : member ID (10 digits) — same as RM[0-9]
//   pos 22-29: insurance start date (DDMMYYYY)
function parseSBFile(content) {
  const bytes = new Uint8Array(content);
  const map = new Map();
  const LINE_LEN = 2000;
  const stride = (bytes[2000] === 0x0D) ? 2002 : 2001;
  for (let offset = 0; offset + LINE_LEN <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + LINE_LEN);
    const memberId = String.fromCharCode(...line.subarray(6, 16));
    if (!/^\d{10}$/.test(memberId)) continue;
    const d8 = String.fromCharCode(...line.subarray(22, 30));
    const startDate = formatDOB(d8);
    if (startDate) map.set(memberId, { startDate });
  }
  return map;
}

// Parse the RM*.05R file. spMap is built from the SP file, sbMap from SB.
function parseRMFile(content, spMap = new Map(), sbMap = new Map()) {
  const bytes = new Uint8Array(content);
  const members = [];
  const LINE_LEN = 2000;

  let stride = LINE_LEN;
  if (bytes[2000] === 0x0D && bytes[2001] === 0x0A) stride = 2002;
  else if (bytes[2000] === 0x0A) stride = 2001;

  for (let offset = 0; offset + LINE_LEN <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + LINE_LEN);
    const rec = parseRMRecord(line, spMap);
    if (rec) {
      // Merge SB start date
      const sb = sbMap.get(rec.rawId);
      if (sb) rec.startDate = sb.startDate;
      members.push(rec);
    }
  }
  return members;
}

// Scan the first line terminator to determine stride (line_len + newline bytes).
function detectStride(bytes) {
  for (let i = 50; i < Math.min(bytes.length, 10000); i++) {
    if (bytes[i] === 0x0D && bytes[i + 1] === 0x0A) return i + 2; // CRLF
    if (bytes[i] === 0x0A) return i + 1;                            // LF only
  }
  return 2001; // fallback
}

function parseRBFile(content) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 82) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const map = new Map();

  for (let offset = 0; offset + 80 <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + Math.min(lineLen, bytes.length - offset));

    const policyStr = String.fromCharCode(...line.subarray(0, 16));
    if (!/^\d{16}$/.test(policyStr)) continue;

    const basePolicy = policyStr.slice(0, 15);
    const suffix     = parseInt(policyStr.slice(15), 10);
    const productCode = String.fromCharCode(...line.subarray(16, 20)).trim();

    let insuredTaz = null;
    if (line.length > 78) {
      const tazRaw = String.fromCharCode(...line.subarray(70, 79));
      if (/^\d{9}$/.test(tazRaw) && tazRaw !== '000000000') insuredTaz = tazRaw;
    }

    const startRaw = String.fromCharCode(...line.subarray(22, 30));
    const startDate = /^\d{8}$/.test(startRaw) ? formatDOB(startRaw) : null;

    const durRaw = String.fromCharCode(...line.subarray(32, 34));
    const duration = /^\d{2}$/.test(durRaw) ? parseInt(durRaw, 10) : null;

    const sumRaw = String.fromCharCode(...line.subarray(34, 43));
    const sumInsured = /^\d{9}$/.test(sumRaw) ? parseInt(sumRaw, 10) / 100 : 0;

    const premRaw = String.fromCharCode(...line.subarray(46, 52));
    const monthlyPremium = /^\d{6}$/.test(premRaw) ? Math.round(parseInt(premRaw, 10)) / 1000 : 0;

    let fundName = '';
    if (line.length > 52) {
      const raw = decodeHebField(line.subarray(50, Math.min(line.length, 150)));
      fundName = raw
        .split(/\s{4,}/)[0]
        .replace(/\s+\d{1,3}\s*$/, '')
        .trim();
    }
    if (!fundName) fundName = productCode ? `ביטוח ${productCode}` : 'ביטוח';

    if (!map.has(basePolicy)) {
      map.set(basePolicy, { taz: null, ownerSuffix: Infinity, memberId: policyStr.slice(6, 16), coverages: [] });
    }
    const entry = map.get(basePolicy);

    if (suffix < entry.ownerSuffix && insuredTaz) {
      entry.ownerSuffix = suffix;
      entry.taz = insuredTaz;
      entry.memberId = policyStr.slice(6, 16);
    }

    if (!entry.coverages.find(c => c.productCode === productCode && c.insuredTaz === insuredTaz)) {
      entry.coverages.push({ name: fundName, productCode, monthlyPremium, sumInsured, startDate, duration, insuredTaz });
    }
  }

  const byMember = new Map();
  for (const entry of map.values()) {
    byMember.set(entry.memberId, { taz: entry.taz, coverages: entry.coverages });
  }
  return byMember;
}

export const hachsharaTrackMap = {
  "ילין כללי": "9302",
  "ילין מניות": "9301",
  "ילין אגח": "9300",
  "ילין אג\"ח": "9300",
  "אנליסט כללי": "13498",
  "אנליסט מניות": "13500",
  "אנליסט אגח": "13499",
  "אנליסט אג\"ח": "13499",
  "מיטב כללי": "9719",
  "מיטב מניות": "9720",
  "מיטב אגח": "9721",
  "מיטב אג\"ח": "9721",
  "אלטשולר כללי": "143",
  "אלטשולר מניות": "142",
  "אלטשולר אגח": "141",
  "אלטשולר אג\"ח": "141",
  "מור כללי": "12937",
  "מור מניות": "12938",
  "מור אגח": "12936",
  "מור אג\"ח": "12936",
  "כספי": "8530",
  "אגח ממשלות": "57",
  "אג\"ח ממשלות": "57",
  "אשראי ואגח": "14425",
  "אשראי ואג\"ח": "14425",
  "עוקב סנפ": "15108",
  "S&P": "15108",
  "עוקב מדדים": "14406",
  "עוקב מניות": "15826",
  "משולב סחיר": "14318",
  "מניות": "58" // fallback
};

// Extract company code and report date from a Hachshara ZIP filename.
// Format: Ild_prod_1_{COMPANYCODE}_{DDMMYYYY}.zip
function parseFileName(fileName) {
  const m = fileName.match(/Ild_prod_\d+_(\d+)_(\d{2})(\d{2})(\d{4})/i);
  if (!m) return { companyCode: null, reportDate: null };
  return {
    companyCode: m[1],
    reportDate: `${m[4]}-${m[3]}-${m[2]}`, // YYYY-MM-DD
  };
}

// Main entry point called from useHachsharaBatchLoader.
// zipFiles: array of { name: string, content: ArrayBuffer }
// Returns: { companyCode, reportDate, fileName, members[] }
export function parseHachsharaZip(fileName, zipFiles) {
  const { companyCode, reportDate } = parseFileName(fileName);

  const rmEntry = zipFiles.find(f => /^RM\d+\.\d+R$/i.test(f.name));
  const rbEntry = zipFiles.find(f => /^RB\d+$/i.test(f.name));
  if (!rmEntry && !rbEntry) throw new Error('לא נמצא קובץ RM או RB בתוך ה-ZIP');

  // SP file provides personal info (memberId → { taz, name, city, gender, DOB })
  const spEntry = zipFiles.find(f => /^SP\d+$/i.test(f.name));
  const spMap = spEntry ? parseSPFile(spEntry.content) : new Map();

  // SB file provides insurance start date (memberId → { startDate })
  const sbEntry = zipFiles.find(f => /^SB\d+$/i.test(f.name));
  const sbMap = sbEntry ? parseSBFile(sbEntry.content) : new Map();

  const members = rmEntry ? parseRMFile(rmEntry.content, spMap, sbMap) : [];

  if (rbEntry) {
    const rbMap = parseRBFile(rbEntry.content);
    for (const [memberId, rbData] of rbMap) {
      if (!rbData.coverages?.length) continue;
      
      const personal = spMap.get(memberId) || {};
      const sb       = sbMap.get(memberId);
      const taz      = rbData.taz || personal.taz || (memberId.startsWith('0') ? memberId.slice(1) : memberId.slice(0, 9));

      const funds = rbData.coverages.map(c => ({
        name: c.name,
        balance: 0,
        productCode: c.productCode,
        monthlyPremium: c.monthlyPremium || 0,
        sumInsured: c.sumInsured || 0,
        startDate: c.startDate || null,
        duration: c.duration || null,
        insuredTaz: c.insuredTaz || null,
      }));

      members.push({
        rawId: memberId,
        taz,
        firstName: personal.firstName || null,
        lastName: personal.lastName || null,
        city: personal.city || null,
        gender: personal.gender || null,
        birthDate: personal.birthDate || null,
        startDate: sb?.startDate || null,
        productType: 'health',
        funds,
      });
    }
  }

  return { companyCode, reportDate, fileName, members };
}
